import * as React from "react";
import {cx} from "emotion";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload, faSpinner} from "@fortawesome/free-solid-svg-icons";

import "./mailed-status.scss";

import {cs}              from "@Common/react/chain-services";
import {consumeContext}  from "@Common/react/context";
import {Load2}           from "@Common/react/load2";
import {UseState}        from "@Common/react/use-state";
import {DataTableD1}     from "@Common/ui-components/heavy/common/design2/data-table/data-table-d1";
import {PageList}        from "@Common/ui-components/heavy/common/design2/pagination-ui/page-list/page-list";
import {PageSize}        from "@Common/ui-components/heavy/common/design2/pagination-ui/page-size/page-size";
import {formatNumberBig} from "@Common/ui-components/language/format-number-big";
import {oMapToArr}       from "@Common/utils/objects";
import {TracingSummary}  from "./tracing-summary/tracing-summary";

export const MailedStatusContent = ({itemId}) => cs(
    consumeContext("apis"),
    ["pageSize", (_, next) => UseState({initValue: 10, next})],
    ["tracingData", ({apis}, next) => Load2({
        fetch: async () => apis.next.item.getTracing({itemId}),
        next,
    })],
    ["downloading", (_, next) => UseState({initValue: false, next})],
    consumeContext("company"),
    ["allowAddressListDownload", ({company}, next) => next(company?.prefs?.allowAddressListDownload)],
    ({pageSize, tracingData, allowAddressListDownload, downloading, apis}) => (
        <div className="mailed-status-content-b8d1 mailed-status-content">
            <div className="header">
                <div className="left">
                    <div className="labels">
                        <div className="label">Mail Status Tracking</div>
                        <div className="sub-label">{`Order ID ${itemId}`}</div>
                    </div>
                    {allowAddressListDownload && (
                        <a {...{
                            className: "csv-download",
                            onClick: async () => {
                                downloading.onChange(true);
                                const result = await apis.next.item.getTracingDownload({itemId});
                                const blob = new Blob([await result.text()], {type: 'text/csv'});
                                const filename = "Scan_data_status_" + itemId + ".csv";
                                var link = document.createElement("a");
                                link.setAttribute("href", window.URL.createObjectURL(blob));
                                link.setAttribute("download", `${filename}`);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                downloading.onChange(false);
                            },
                        }}>
                            <span>
                                <FontAwesomeIcon
                                    key={itemId + " download"}
                                    className={cx("download-icon", {"fa-pulse": downloading.value})}
                                    icon={downloading.value ? faSpinner : faDownload}/>
                            </span>
                            <span className="text">Download</span>
                        </a>
                    )}
                </div>
                <div className="right">
                    <img src={require("./usps.svg")} alt="USPS"/>
                </div>
            </div>
            {!tracingData.loading && tracingData.value[itemId]?.warning && (
                <div className="warning">
                    <div className="icon">
                        <img src={require("./warning.svg")}/>
                    </div>
                    {tracingData.value[itemId].warning}
                </div>
            )}
            {tracingData.loading ? (
                <div className="middle" xd-qa-type="label" xd-qa-name="Loading">Loading...</div>
            ) : !tracingData.loading && (tracingData.value[itemId]?.error || !tracingData.value[itemId]) ? (
                <div className="middle" xd-qa-type="label" xd-qa-name="Error">
                    Mailing info unavailable. Please try again later.
                </div>
            ) : (
                <div className="content">
                    {TracingSummary({
                        statusCounts: tracingData.value[itemId]?.summary,
                        totalRecords: typeof(tracingData.value) === "object" ? oMapToArr(tracingData.value[itemId].detail).length : 0,
                    })}
                    <div className="recipient-table">
                        {DataTableD1({
                            paginationKey: "mailedStatus",
                            pageSize: pageSize.value,
                            rows: typeof (tracingData.value) === "object" && oMapToArr(tracingData.value[itemId].detail, r => ({
                                columns: [
                                    {
                                        render: _ => r?.Name,
                                        sortValue: _ => r?.Name,
                                    },
                                    {
                                        render: _ => <>
                                            <div className="addressL1">
                                                {r?.AddressL1}
                                            </div>
                                            <div className="addressL2">
                                                {r?.AddressL2}
                                            </div>
                                        </>,
                                        sortValue: _ => r?.AddressL2,
                                    },
                                    {
                                        render: _ => <>
                                            <div className="date">
                                                {mailedFormatDate(r?.QrScanDate)?.date}
                                            </div>
                                            <div className="time">
                                                {mailedFormatDate(r?.QrScanDate)?.time}
                                            </div>
                                        </>,
                                        sortValue: _ => r?.QrScanDate,
                                    },
                                    {
                                        render: _ => <>
                                            <div className="date">
                                                {mailedFormatDate(r?.ClickTime)?.date}
                                            </div>
                                            <div className="time">
                                                {mailedFormatDate(r?.ClickTime)?.time}
                                            </div>
                                        </>,
                                        sortValue: _ => r?.ClickTime,
                                    },
                                    {
                                        render: _ => r?.Status || "On the Way",
                                        sortValue: _ => r?.Status,
                                    },
                                    {
                                        render: _ => <>
                                            <div className="date">
                                                {mailedFormatDate(r?.LastScanDateTime)?.date}
                                            </div>
                                            <div className="time">
                                                {mailedFormatDate(r?.LastScanDateTime)?.time}
                                            </div>
                                        </>,
                                        sortValue: _ => r?.LastScanDateTime,
                                    },
                                    {
                                        render: _ => r?.IMB,
                                        sortValue: _ => r?.IMB,
                                    },
                                ].filter(v => v),
                            })),
                            columns,
                            emptyAction: {
                                label: "Mailing info unavailable. Please try again later."
                            },
                            getFooter: ({colCount, pagination}) => ({
                                columns: [{
                                    colSpan: colCount,
                                    render: () => (
                                        <div className="controls">
                                            {PageSize({state: pageSize})}
                                            {pagination.active && PageList(pagination)}
                                            <div className="page-summary">
                                                <div className="page-rows-list">
                                                    {getPageSummary({
                                                        pageIndex: pagination.state.value,
                                                        pageSize: pageSize.value,
                                                        count: typeof(tracingData.value) === "object" ? oMapToArr(tracingData.value[itemId].detail).length : 0,
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }],
                            }),
                        })}
                    </div>
                </div>
            )}
        </div>
    ),
);

const columns = [
    {
        label: "RECIPIENT NAME",
        className: "recipient-name",
        sortable: {
            defaultDirection: "asc",
        },
        width: "150px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "RECIPIENT NAME",
        },
    },
    {
        label: "RECIPIENT ADDRESS",
        className: "recipient-address",
        sortable: {
            defaultDirection: "asc",
        },
        width: "202px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "RECIPIENT ADDRESS",

        },
    },
    {
        label: "QR SCAN",
        className: "qr-scan",
        sortable: {
            defaultDirection: "desc",
        },
        width: "106px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "QR SCAN",
        },
    },
    {
        label: "ID CLICK TIME" ,
        className: "click-time",
        sortable: {
            defaultDirection: "desc",
        },
        width: "126px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "ID CLICK TIME",
        },
        toolTip: "Indicates the time recipient clicked on the Informed Delivery email",
    },
    {
        label: "STATUS",
        className: "status",
        sortable: {
            defaultDirection: "asc",
        },
        width: "83px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "STATUS",
        },
    },
    {
        label: "LAST UPDATE",
        className: "last-update",
        sortable: {
            defaultDirection: "asc",
        },
        width: "106px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "LAST UPDATE",
        },
    },
    {
        label: "INTELLIGENT MAIL BARCODE",
        className: "imb",
        sortable: {
            defaultDirection: "asc",
        },
        width: "189px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "INTELLIGENT MAIL BARCODE",
        },
    },
];
const getPageSummary = ({pageIndex, pageSize, count}) => {
    return (
        formatNumberBig(pageIndex * pageSize + 1) +
        "-" +
        formatNumberBig(
            Math.min(count, (pageIndex + 1) * pageSize)
        ) +
        " of " + count

    );
};
const mailedFormatDate = dateTime => {
    const oldDate = dateTime?.split(" ")?.[0];
    const hour = dateTime?.split(" ")?.[1]?.slice(0, 2);
    return oldDate && {
        date: oldDate.split("-")[1] + "-" + oldDate.split("-")[2] + "-" + oldDate.split("-")[0],
        time: hour > 12 ? (hour - 12 < 10 ? "0" : "") + (hour - 12) + dateTime.split(" ")?.[1]?.slice(2, 5) + " PM" :
              dateTime.split(" ")?.[1]?.slice(0, 5) + " AM",
    };
};